/**
 * GraphQL operations for the `redact-tdo-status-history` SDO (VE-26730): the
 * registry/schema gate lookup, the append-only row write, and the paged read.
 *
 * All three are fully parameterized — no interpolation, and never any note text
 * outside the `data` variable of the write.
 */

/**
 * Registry → published schema id. Deliberately this module's own query rather
 * than notification's getLatestSchemaId helper: that helper dispatches
 * SCHEMA_ID_NOT_FOUND, which a global watcher turns into an error snackbar, and
 * an unprovisioned status-history registry must fail silently (see the gate
 * contract in ./index.ts).
 */
export const resolveSchemaIdQuery = `
  query resolveTdoStatusUpdatesSchemaId($dataRegistryId: ID!) {
    dataRegistry(id: $dataRegistryId) {
      id
      publishedSchema {
        id
      }
    }
  }
`;

export interface ResolveSchemaIdResponse {
  readonly dataRegistry?: {
    readonly id?: string;
    readonly publishedSchema?: {
      readonly id?: string;
    } | null;
  } | null;
}

export const writeStatusHistoryRowQuery = `
  mutation writeTdoStatusHistoryRow($id: ID!, $schemaId: ID!, $data: JSONData!) {
    createStructuredData(input: {
      id: $id,
      schemaId: $schemaId,
      data: $data
    }) {
      id
      data
      createdDateTime
      modifiedDateTime
    }
  }
`;

export interface WriteStatusHistoryRowResponse {
  readonly createStructuredData: {
    readonly id: string;
    readonly data: Record<string, unknown>;
    readonly createdDateTime: string;
    readonly modifiedDateTime: string;
  };
}

export const fetchStatusHistoryQuery = `
  query fetchTdoStatusHistory($schemaId: ID!, $tdoId: ID!, $limit: Int!, $offset: Int!) {
    structuredDataObjects(
      schemaId: $schemaId
      filter: {
        tdoId: $tdoId
      }
      orderBy: [{ field: createdDateTime, direction: desc }]
      limit: $limit
      offset: $offset
    ) {
      count
      records {
        id
        createdDateTime
        data
      }
    }
  }
`;

export interface FetchStatusHistoryResponse {
  readonly structuredDataObjects: {
    readonly count: number;
    readonly records: readonly {
      readonly id: string;
      readonly createdDateTime: string;
      readonly data: Record<string, unknown>;
    }[];
  };
}

/**
 * Batch soft-delete: one aliased mutation per chunk of rows, matching the
 * pattern the trash and dashboard SDO paths already use. `createStructuredData`
 * with an explicit id is an UPSERT (same as the comments module), so each entry
 * re-writes its row with `isDeleted: true` added.
 *
 * Built as a string rather than parameterised because the alias count varies
 * per call; the only interpolated values are server-issued uuids and the
 * schema id.
 */
export const buildSoftDeleteRowsQuery = (
  rows: readonly { readonly id: string }[]
): string => `
  mutation softDeleteTdoStatusHistoryRows($schemaId: ID!, ${rows
    .map((_, index) => `$data${index}: JSONData!`)
    .join(', ')}) {
    ${rows
      .map(
        (row, index) => `
    r${index}: createStructuredData(input: {
      id: "${row.id}",
      schemaId: $schemaId,
      data: $data${index}
    }) {
      id
    }`
      )
      .join('\n')}
  }
`;

/**
 * Batch hard-delete: used when the TDO itself is gone, so there is nothing left
 * for the rows to belong to and nothing to preserve. Same aliased shape as the
 * soft-delete above, and as the trash and dashboard SDO paths.
 */
export const buildDeleteRowsQuery = (rowIds: readonly string[]): string => `
  mutation deleteTdoStatusHistoryRows($schemaId: ID!) {
    ${rowIds
      .map(
        (rowId, index) => `
    r${index}: deleteStructuredData(input: {
      id: "${rowId}",
      schemaId: $schemaId
    }) {
      id
    }`
      )
      .join('\n')}
  }
`;
